type LogLevel = "info" | "warn" | "error" | "debug";
type ConsoleMethod = (...args: any[]) => void;

interface LogOptions {
  context?: Record<string, any>;
  tags?: Record<string, string>;
}

export interface LogFn {
  (message?: any, ...optionalParams: any[]): void;
}
/**
 * Класс для логирования с поддержкой группировки сообщений и стилизации вывода.
 * Также включает интеграцию с Sentry для отслеживания ошибок в production.
 *
 * @example
 * // Создание логгера с именем группы
 * const logger = new Logger(undefined, false);
 *
 * // Базовое использование
 * logger.log('Сообщение'); // [MyComponent] Сообщение
 * logger.warn('Предупреждение'); // [MyComponent] ⚠️ Предупреждение
 * logger.error('Ошибка'); // [MyComponent] 🔴 Ошибка
 *
 * // Логирование с дополнительными параметрами
 * logger.log('Данные пользователя:', { id: 1, name: 'John' });
 *
 * // В production режиме ошибки автоматически отправляются в Sentry
 * logger.error(new Error('Критическая ошибка'));
 *
 * // Отключение логирования
 * const silentLogger = new Logger(undefined, false);
 * silentLogger.log('Это сообщение не будет выведено');
 */
export class Logger {
  /** Имя группы для логов */
  private groupName?: string;

  /** Метод для вывода информационных сообщений */
  readonly log: ConsoleMethod;
  /** Метод для вывода предупреждений */
  readonly warn: ConsoleMethod;
  /** Метод для вывода ошибок */
  readonly error: ConsoleMethod;

  /**
   * @param groupName - Имя группы для логов. Если не указано, определяется автоматически
   * @param enable - Включить/выключить логирование. По умолчанию включено
   */
  constructor(
    groupName: string | undefined = undefined,
    enable: boolean = true
  ) {
    this.groupName = groupName || this.getCallerLocation().split(" ")[0];
    if (enable) {
      this.log = this.getConsoleMethod("info").bind(
        console,
        ...this.getLogArgs("info")
      );
    } else {
      this.log = () => {};
    }
    this.warn = this.getConsoleMethod("warn").bind(
      console,
      ...this.getLogArgs("warn")
    );
    this.error = this.getConsoleMethod("error").bind(
      console,
      ...this.getLogArgs("error")
    );
  }
  /*
    ⚠️ Важно
    Это работает только в dev-сборке, где Error.stack читаемый
    В проде Error.stack часто обфусцируется или удаляется — поэтому мы используем groupName = "App" по умолчанию
    */
  /**
   * Определяет место вызова логгера в коде
   * @returns Строка с информацией о месте вызова
   */
  getCallerLocation(): string {
    const err = new Error();
    const stack = err.stack?.split("\n");

    if (!stack || stack.length < 4) return "Unknown";

    const callerLine = stack[3]; // строка с вызовом
    // Пример: "    at useSelection (http://localhost:8000/src/hooks/useSelection.ts:5:18)"
    // Или:    "    at http://localhost:8000/src/hooks/useSelection.ts:5:18"

    const match = callerLine.match(/at\s+(.*)/);
    if (!match) return "Unknown";

    const raw = match[1].trim();

    // Если в raw есть пробел — значит это "functionName (url)"
    // Если нет — это просто url
    if (raw.includes(" ")) {
      const [func, loc] = raw.split(" ");
      return func;
    }

    // Если это просто ссылка — вытащим имя файла
    const fileMatch = raw.match(/\/([^\/]+)\.([jt]sx?):\d+:\d+/);
    if (fileMatch) {
      return fileMatch[1]; // например: "useSelection"
    }

    // Если строка — это ссылка, попробуем достать имя файла .tsx
    const fileMatchUrl = raw.match(/\/([^\/]+)\.tsx/); // например, EditorAllBlocks.tsx
    if (fileMatchUrl) {
      return fileMatchUrl[1]; // EditorAllBlocks
    }
    return "Unknown";
  }

  /**
   * Возвращает соответствующий метод консоли для заданного уровня логирования
   * @param level - Уровень логирования
   */
  private getConsoleMethod(level: LogLevel): ConsoleMethod {
    switch (level) {
      case "info":
        return console.info;
      case "warn":
        return console.warn;
      case "error":
        return console.error;
      case "debug":
      default:
        return console.debug;
    }
  }

  /**
   * Возвращает CSS-стили для форматирования вывода в консоль
   * @param level - Уровень логирования
   */
  private getStyle(level: LogLevel): string {
    return {
      info: "color: #5badeb; font-weight: bold",
      warn: "color: #e1b12c; font-weight: bold",
      error: "color: #d63031; font-weight: bold",
      debug: "color: #636e72; font-weight: bold",
    }[level];
  }

  /**
   * Формирует массив аргументов для вывода в консоль
   * @param level - Уровень логирования
   * @param args - Дополнительные аргументы для вывода
   */
  private getLogArgs(level: LogLevel, ...args: any[]): any[] {
    if (this.groupName) {
      return [`%c${this.groupName}`, this.getStyle(level), ...args];
    }
    return [`%c${level}`, this.getStyle(level), ...args];
  }

  /**
   * Обрабатывает и логирует исключения. В production отправляет их в Sentry
   * @param err - Объект ошибки или строка с описанием
   * @param context - Контекст ошибки
   * @param extra - Дополнительные данные для логирования
   */
  exception(err: unknown, context?: string, extra?: Record<string, any>) {
    const message = `[${this.groupName}] Exception: ${context ?? "Unknown"}`;
    const errObj = typeof err === "string" ? new Error(err) : err;

    console.error(message, errObj, extra);
  }
}
